import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Divider,
  InputBase,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { PostfixOperandSchema, PostfixOptions, PreviousStatePredicate } from 'components/PostfixNew/types';
import React, { useEffect, useState } from 'react';

import { FilledTextField } from 'pages/data-export/dataExport.styles';

import { PostfixOperandProperty, UnwrapPostfixOptionFunctionTypes } from '../../types';
import { SelectOption, SelectTabs } from '../Select/types';

import { useController } from './index.controller';
import { PostfixAutocompleteProperty } from './types';
import { useAutocompleteContext } from '.';

export type OperandAutocompleteOptions = UnwrapPostfixOptionFunctionTypes<PostfixAutocompleteProperty['options']>;

export type OperandAutocompleteProps<T extends PostfixOptions> = {
  property: PostfixOperandProperty;
  // value: PostfixOperandSchema<{
  //   properties: PostfixAutocompleteProperty[];
  // }>;
  currentValue?: unknown;
  onChange: PreviousStatePredicate<PostfixOperandSchema<T>>;
  compact?: boolean;
  options: OperandAutocompleteOptions;
  style?: 'input' | 'select';
};

const blankValue: SelectOption = {
  label: '',
  value: '__blankValue',
  //@ts-expect-error force set arbitrary property
  blankValue: true,
};

export function View<T extends PostfixOptions>(props: OperandAutocompleteProps<T>) {
  const state = useController(props);
  const [open, setOpen] = useState(false);

  if (!state) return null;

  const { options, value, compact, customInput, hidden, multiple, error, listBoxComponent, style } = state;

  if (hidden) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexGrow={style === 'select' ? 0 : 1}
      flexShrink={0}
      width={style === 'input' ? 'min-content' : undefined}
    >
      {options.startDropdownAdornment && (
        <AutocompleteDropdownAdornmentGroup options={options.startDropdownAdornment} />
      )}
      <Autocomplete
        open={open}
        disabled={options.disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        componentsProps={{
          paper: {
            elevation: 4,
          },
          popper: {
            sx: {
              minWidth: 300,
            },
          },
        }}
        multiple={multiple}
        options={value.list.length ? value.list : [blankValue]}
        // sx={{
        //   minWidth: 400,
        // }}
        fullWidth
        value={
          multiple
            ? value.current
              ? Array.isArray(value.current)
                ? value.current
                : [value.current]
              : []
            : value.current[0] ?? null
        }
        key={props.property.name}
        freeSolo={customInput?.enabled}
        inputValue={customInput?.current}
        size={compact ? 'small' : 'medium'}
        groupBy={(option) => option.group ?? ''}
        onInputChange={(_, newValue) => {
          customInput?.onChange(newValue);
        }}
        getOptionKey={(option) => (typeof option === 'string' ? option : option.value)}
        getOptionLabel={(option) => (typeof option == 'string' ? option : option.label)}
        renderInput={(params) =>
          style === 'input' ? (
            <FilledTextField
              {...params}
              label={options.label}
              error={error || customInput?.error}
              sx={{
                ...(options.startDropdownAdornment
                  ? {
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                    }
                  : {}),
              }}
            />
          ) : (
            <>
              <InputBase
                inputProps={{
                  ...params.inputProps,
                }}
                aria-hidden={true}
                sx={{
                  display: 'none',
                  minWidth: 100,
                }}
              />
              <Button
                ref={params.InputProps.ref}
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => {
                  e.currentTarget.focus();
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                sx={{
                  flexGrow: 0,
                  minWidth: 100,
                }}
              >
                {options.labelPrefix}
                {value.current.length ? value.current[0].label : options.label}
              </Button>
            </>
          )
        }
        onChange={(_, newValue, reason) => {
          if (reason === 'clear') return;
          if (typeof newValue === 'string') {
            // ignore, we use onBlur to handle this
          } else {
            value.onChange((prev) => ({
              ...prev,
              properties: {
                ...prev.properties,
                [props.property.name]: Array.isArray(newValue)
                  ? newValue.map((x) => (typeof x === 'string' ? x : x.value))
                  : newValue?.value,
              },
            }));
          }
        }}
        onBlur={() => {
          if (customInput && customInput.current.length && !customInput?.fromSelection && !multiple) {
            if (customInput.error) {
              value.onChange((prev) => ({
                ...prev,
                properties: {
                  ...prev.properties,
                  [props.property.name]: null,
                },
              }));
            } else {
              value.onChange((prev) => ({
                ...prev,
                properties: {
                  ...prev.properties,
                  [props.property.name]:
                    customInput.type === 'number' ? parseFloat(customInput.current) : customInput.current,
                },
              }));
            }
          }
        }}
        renderOption={(props, option, _, ownerState) => (
          <ListItem
            {...props}
            key={props.key}
            disabled={option.disabled}
            onClick={(e) => {
              if (option.disabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              props.onClick?.(e);
            }}
          >
            {option.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  marginRight: 1,
                }}
              >
                {option.icon}
              </ListItemIcon>
            )}
            <ListItemText sx={{ my: 0 }} primary={ownerState.getOptionLabel(option)} secondary={option.description} />
          </ListItem>
        )}
        ListboxComponent={listBoxComponent}
        // renderOption={forwardRef<HTMLLIElement | null, any>(function AutocompleteOption(props, ref) {
        //   return <ListItem ref={ref} {...props} />;
        // })}
      />
    </Stack>
  );
}

function AutocompleteDropdownAdornmentGroup(props: { options: SelectTabs }) {
  const { options } = props;
  const autocompleteContext = useAutocompleteContext();

  const currentValue = autocompleteContext?.value[options.property];

  useEffect(() => {
    if (!autocompleteContext) return;

    if (!currentValue && options.options.length > 0) {
      autocompleteContext.onChange((prev) => ({
        ...prev,
        properties: {
          ...prev.properties,
          [options.property]: options.options[0].value,
        },
      }));
    }
  }, [currentValue, autocompleteContext, options.options, options.property]);

  if (!autocompleteContext) return null;

  return (
    <>
      <FilledTextField
        select
        value={currentValue ?? ''}
        onChange={(e) => {
          autocompleteContext.onChange((prev) => ({
            ...prev,
            properties: {
              ...prev.properties,
              [options.property]: e.target.value,
            },
          }));
        }}
        sx={{
          flexShrink: 0,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '& .MuiOutlinedInput-notchedOutline': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '& :not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderRight: 'none',
          },
        }}
      >
        {options.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </FilledTextField>
    </>
  );
}

export function AutocompleteTabsGroup(props: { tabs: SelectTabs }) {
  const { tabs } = props;
  const selectContext = useAutocompleteContext();

  const currentValue = selectContext?.value[tabs.property];

  useEffect(() => {
    if (!selectContext) return;

    const availableTabs = tabs.options.filter((tab) => !tab.disabled);

    if (availableTabs.length) {
      const selectedTab = availableTabs.find((tab) => tab.value === currentValue);

      if (!selectedTab) {
        selectContext.onChange((prev) => ({
          ...prev,
          properties: {
            ...prev.properties,
            [tabs.property]: availableTabs[0].value,
          },
        }));
      }
    }
  }, [currentValue, selectContext, tabs.options, tabs.property]);

  if (!selectContext) return null;

  return (
    <>
      <ToggleButtonGroup
        size="small"
        sx={{
          px: 1,
          pb: 1,
          width: '100%',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '& > *': {
            flexGrow: 1,
          },
        }}
        value={currentValue}
        onChange={(_, newValue) =>
          selectContext.onChange((prev) => ({
            ...prev,
            properties: {
              ...prev.properties,
              [tabs.property]: newValue,
            },
          }))
        }
        exclusive
      >
        {tabs.options.map((tab) => (
          <ToggleButton key={tab.value} value={tab.value} disabled={tab.disabled}>
            {tab.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Divider />
    </>
  );
}
